import type { ReactNode } from 'react';
import AppShell from './AppShell';
import Stepper from './Stepper';
import Button from './Button';
import Callout from './Callout';
import type { SaveState } from '../lib/useProject';

export const WIZARD_STEPS = [
  'Building type',
  'Questions',
  'Size',
  'System',
  'Styles',
  'Your style',
  'Concepts',
];

interface WizardShellProps {
  step: number;
  verticalLabel?: string;
  title: string;
  intro?: string;
  saveState: SaveState;
  children: ReactNode;
  onBack?: () => void;
  backLabel?: string;
  onContinue?: () => void;
  continueLabel?: string;
  /** When set, Continue is disabled and this explains why. */
  blockedReason?: string;
  busy?: boolean;
}

function SaveIndicator({ state }: { state: SaveState }) {
  if (state === 'idle') return null;
  const copy: Record<Exclude<SaveState, 'idle'>, string> = {
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Not saved — we will keep trying',
  };
  return (
    <p
      className={state === 'error' ? 't-meta font-semibold text-alert' : 't-meta text-grey-600'}
      aria-live="polite"
    >
      {copy[state]}
    </p>
  );
}

export default function WizardShell({
  step,
  verticalLabel,
  title,
  intro,
  saveState,
  children,
  onBack,
  backLabel = 'Go back',
  onContinue,
  continueLabel = 'Continue',
  blockedReason,
  busy = false,
}: WizardShellProps) {
  return (
    <AppShell action={<SaveIndicator state={saveState} />}>
      <Stepper steps={WIZARD_STEPS} current={step} />

      <div className="mt-10">
        {verticalLabel && <p className="t-eyebrow text-green-600">{verticalLabel}</p>}
        <h1 className="t-h1 mt-3 text-ink">{title}</h1>
        {intro && <p className="t-body-lg measure mt-4 text-grey-600">{intro}</p>}
      </div>

      <div className="mt-10">{children}</div>

      {blockedReason && (
        <div className="mt-8">
          <Callout title="Before you continue">{blockedReason}</Callout>
        </div>
      )}

      <div className="mt-10 flex flex-col-reverse gap-3 border-t border-grey-200 pt-8 sm:flex-row sm:items-center sm:justify-between">
        {onBack ? (
          <Button variant="secondary" block onClick={onBack}>
            {backLabel}
          </Button>
        ) : (
          <span />
        )}
        {onContinue && (
          <Button
            block
            onClick={onContinue}
            disabled={Boolean(blockedReason) || busy}
            aria-busy={busy}
          >
            {busy ? 'Saving…' : continueLabel}
          </Button>
        )}
      </div>
    </AppShell>
  );
}

/** Shared loading and not-found states so every wizard screen behaves alike. */
export function WizardStatus({ message }: { message: string }) {
  return (
    <AppShell>
      <p className="t-body text-grey-600">{message}</p>
    </AppShell>
  );
}
